import { useCallback } from "react";
import { ComparisonOperator } from "@evergis/ui";

import { NUMERIC_ATTRIBUTE_TYPES } from "../../../evergis/core/condition/createCondition";

import { getComparisonOperatorByType } from "../utils/getComparisonOperatorOptions";
import { checkEmptyOrFilled } from "../../../utils/checkEmptyOrFilled";

import { FilterConditionType, DeleteCondition, UpdateCondition, LayerFilterOption } from "../types";

interface FilterConditionUpdate {
  changeAttribute: ([{ value, type }]: LayerFilterOption<string>[]) => void;
  changeComparisonOperator: ([{ value }]: LayerFilterOption<ComparisonOperator>[]) => void;
  changeValue: (value: string | number) => void;
  deleteCurrentCondition: VoidFunction;
}

export const useFilterConditionUpdate = (
  condition: FilterConditionType,
  updateCondition: UpdateCondition,
  deleteCondition: DeleteCondition,
): FilterConditionUpdate => {
  const { id } = condition;

  const changeAttribute = useCallback(
    ([{ value: attribute, type }]: LayerFilterOption<string>[]) => {
      const comparisonOperator = resolveNewComparisonOperator(condition, { type });
      const conditionValue = resolveNewValue(condition, { type, comparisonOperator });

      if (![ComparisonOperator.Empty, ComparisonOperator.Filled].includes(comparisonOperator)) {
        updateCondition(id, {
          attribute,
          type,
          comparisonOperator,
        });
      } else {
        updateCondition(id, {
          attribute,
          type,
          comparisonOperator,
          value: conditionValue,
        });
      }
    },
    [id, updateCondition, condition],
  );

  const changeComparisonOperator = useCallback(
    ([{ value: comparisonOperator }]: LayerFilterOption<ComparisonOperator>[]) => {
      const value = resolveNewValue(condition, { type: condition.type, comparisonOperator });

      updateCondition(id, {
        value,
        comparisonOperator,
      });
    },
    [id, updateCondition, condition],
  );

  const changeValue = useCallback(
    (value: string | number | null) => updateCondition(id, { value }),
    [id, updateCondition],
  );

  const deleteCurrentCondition = useCallback(() => deleteCondition(id), [id, deleteCondition]);

  return {
    changeAttribute,
    changeComparisonOperator,
    changeValue,
    deleteCurrentCondition,
  };
};

function resolveNewComparisonOperator(
  condition: Partial<FilterConditionType>,
  newCondition: Partial<FilterConditionType>,
): ComparisonOperator {
  if (newCondition.type === undefined) {
    return null;
  }

  const availableOperators = getComparisonOperatorByType(newCondition.type);

  return availableOperators.includes(condition.comparisonOperator)
    ? condition.comparisonOperator
    : ComparisonOperator.Equals;
}

const DEFAULT_VALUES = {
  string: "",
  number: 0,
};

function resolveNewValue(
  condition: Partial<FilterConditionType>,
  newCondition: Partial<FilterConditionType>,
): FilterConditionType["value"] {
  const resetValue = checkEmptyOrFilled(newCondition.comparisonOperator) || newCondition.type === undefined;
  const setDefaults = newCondition.type !== condition.type || checkEmptyOrFilled(condition.comparisonOperator);

  if (resetValue) {
    return null;
  }

  if (setDefaults) {
    return NUMERIC_ATTRIBUTE_TYPES.includes(newCondition.type) ? DEFAULT_VALUES.number : DEFAULT_VALUES.string;
  }

  return condition.value;
}
